import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ItemStatus } from "@focus/focusUI";
import type {
  Contact,
  Organization,
  PhonebookRequestKey,
  PhonebookState,
} from "../types/phonebook";
import { ofType } from "redux-observable";
import { mergeMap, map, catchError, of } from "rxjs";
import { createAsyncReducers } from "./helpers";

const baseURL = "/phonebook/api/v1";

const initialState: PhonebookState = {
  organizations: [],
  contacts: [],
  contactsByOperator: {},
  requests: {
    getContacts: { status: ItemStatus.READY, error: null },
    getContactsByOperatorName: { status: ItemStatus.READY, error: null },
    addContact: { status: ItemStatus.READY, error: null },
    updateContact: { status: ItemStatus.READY, error: null },
    deleteContact: { status: ItemStatus.READY, error: null },
    getOrganizations: { status: ItemStatus.READY, error: null },
    addOrganization: { status: ItemStatus.READY, error: null },
    updateOrganization: { status: ItemStatus.READY, error: null },
    deleteOrganization: { status: ItemStatus.READY, error: null },
  },
};

export const phonebookMessages = {
  contact: {
    createSuccess: "Contact added successfully.",
    updateSuccess: "Contact updated successfully.",
    deleteSuccess: "Contact deleted successfully.",
    loadError: "Could not load contacts.",
    genericError: "Something went wrong with the contact.",
  },
  organization: {
    createSuccess: "Organization created successfully.",
    updateSuccess: "Organization updated successfully.",
    deleteSuccess: "Organization deleted successfully.",
    loadError: "Could not load organizations.",
  },
};

const getOrganizationsAsync = createAsyncReducers<
  PhonebookState,
  PhonebookRequestKey,
  Organization[]
>("getOrganizations", (state, action) => {
  state.organizations = action.payload;
});

const getContactsAsync = createAsyncReducers<
  PhonebookState,
  PhonebookRequestKey,
  Contact[]
>("getContacts", (state, action) => {
  state.contacts = action.payload;
});

const getContactsByOperatorNameAsync = createAsyncReducers<
  PhonebookState,
  PhonebookRequestKey,
  {
    operatorName: string;
    contacts: Contact[];
  },
  { operatorName: string }
>("getContactsByOperatorName", (state, action) => {
  const { operatorName, contacts } = action.payload;
  state.contactsByOperator[operatorName] = contacts;
});

const addContactAsync = createAsyncReducers<
  PhonebookState,
  PhonebookRequestKey,
  Contact,
  { contact: Contact }
>("addContact", (state, action) => {
  state.contacts = [...state.contacts, action.payload];
});

const updateContactAsync = createAsyncReducers<
  PhonebookState,
  PhonebookRequestKey,
  Contact,
  { contact: Contact }
>("updateContact", (state, action) => {
  state.contacts = state.contacts.map((c) =>
    c.id === action.payload.id ? action.payload : c,
  );
});

const deleteContactAsync = createAsyncReducers<
  PhonebookState,
  PhonebookRequestKey,
  unknown,
  { id: number }
>("deleteContact", (state, action) => {
  state.contacts = state.contacts.filter((c) => c.id !== action.payload);
});

const addOrganizationAsync = createAsyncReducers<
  PhonebookState,
  PhonebookRequestKey,
  Organization,
  { organization: Organization }
>("addOrganization", (state, action) => {
  state.organizations = [...state.organizations, action.payload];
});

const updateOrganizationAsync = createAsyncReducers<
  PhonebookState,
  PhonebookRequestKey,
  Organization,
  { organization: Organization }
>("updateOrganization", (state, action) => {
  state.organizations = state.organizations.map((c) =>
    c.id === action.payload.id ? action.payload : c,
  );
});

const deleteOrganizationAsync = createAsyncReducers<
  PhonebookState,
  PhonebookRequestKey,
  unknown,
  { id: number }
>("deleteOrganization", (state, action) => {
  state.organizations = state.organizations.filter(
    (c) => c.id !== action.payload,
  );
});

const phonebookSlice = createSlice({
  name: "phonebook",
  initialState,
  reducers: {
    getOrganizationsRequest: getOrganizationsAsync.request,
    getOrganizationsSuccess: getOrganizationsAsync.success,
    getOrganizationsFailure: getOrganizationsAsync.failure,
    getContactsRequest: getContactsAsync.request,
    getContactsSuccess: getContactsAsync.success,
    getContactsFailure: getContactsAsync.failure,
    getContactsByOperatorNameRequest: getContactsByOperatorNameAsync.request,
    getContactsByOperatorNameSuccess: getContactsByOperatorNameAsync.success,
    getContactsByOperatorNameFailure: getContactsByOperatorNameAsync.failure,
    addContactRequest: addContactAsync.request,
    addContactSuccess: addContactAsync.success,
    addContactFailure: addContactAsync.failure,
    updateContactRequest: updateContactAsync.request,
    updateContactSuccess: updateContactAsync.success,
    updateContactFailure: updateContactAsync.failure,
    deleteContactRequest: deleteContactAsync.request,
    deleteContactSuccess: deleteContactAsync.success,
    deleteContactFailure: deleteContactAsync.failure,
    addOrganizationRequest: addOrganizationAsync.request,
    addOrganizationSuccess: addOrganizationAsync.success,
    addOrganizationFailure: addOrganizationAsync.failure,
    updateOrganizationRequest: updateOrganizationAsync.request,
    updateOrganizationSuccess: updateOrganizationAsync.success,
    updateOrganizationFailure: updateOrganizationAsync.failure,
    deleteOrganizationRequest: deleteOrganizationAsync.request,
    deleteOrganizationSuccess: deleteOrganizationAsync.success,
    deleteOrganizationFailure: deleteOrganizationAsync.failure,
  },
});

export const {
  getOrganizationsRequest,
  getOrganizationsSuccess,
  getOrganizationsFailure,
  getContactsRequest,
  getContactsSuccess,
  getContactsFailure,
  getContactsByOperatorNameRequest,
  getContactsByOperatorNameSuccess,
  getContactsByOperatorNameFailure,
  addContactRequest,
  addContactSuccess,
  addContactFailure,
  updateContactRequest,
  updateContactSuccess,
  updateContactFailure,
  deleteContactRequest,
  deleteContactSuccess,
  deleteContactFailure,
  addOrganizationRequest,
  addOrganizationSuccess,
  addOrganizationFailure,
  updateOrganizationRequest,
  updateOrganizationSuccess,
  updateOrganizationFailure,
  deleteOrganizationRequest,
  deleteOrganizationSuccess,
  deleteOrganizationFailure,
} = phonebookSlice.actions;

const getOrganizationsEpic = (action, _state, { ajax }) => {
  return action.pipe(
    ofType(getOrganizationsRequest.type),
    mergeMap(() => {
      const url = `${baseURL}/organizations`;
      return ajax.getJSON(url).pipe(
        map((organizations: Organization[]) =>
          getOrganizationsSuccess(organizations),
        ),
        catchError((error) => {
          return of(
            getOrganizationsFailure(
              error.message || phonebookMessages.organization.loadError,
            ),
          );
        }),
      );
    }),
  );
};

const addOrganizationEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(addOrganizationRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          organization: Organization;
        }>,
      ) => {
        const { organization } = action.payload;
        const url = `${baseURL}/organizations`;

        return ajax({
          url,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: organization,
          responseType: "json",
        }).pipe(
          mergeMap((response: { response: any; message?: string }) =>
            of(
              addOrganizationSuccess(response.response, {
                notificationOnSuccess:
                  phonebookMessages.organization.createSuccess,
              }),
            ),
          ),
          catchError((error) =>
            of(
              addOrganizationFailure(
                error.response?.message ||
                  error.message ||
                  "Could not create organization.",
              ),
            ),
          ),
        );
      },
    ),
  );

const updateOrganizationEpic = (action$, _state, { ajax }) =>
  action$.pipe(
    ofType(updateOrganizationRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          organization: Organization;
        }>,
      ) => {
        const { organization } = action.payload;
        const url = `${baseURL}/organizations/${organization.id}`;

        return ajax({
          url,
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: organization,
          responseType: "json",
        }).pipe(
          mergeMap((response: { response: any; message?: string }) =>
            of(
              updateOrganizationSuccess(response.response, {
                notificationOnSuccess: "Organization updated successfully.",
              }),
            ),
          ),
          catchError((error) =>
            of(
              updateOrganizationFailure(
                error.response?.message ||
                  error.message ||
                  "Could not update organization.",
              ),
            ),
          ),
        );
      },
    ),
  );

const getContactsEpic = (action, _state, { ajax }) => {
  return action.pipe(
    ofType(getContactsRequest.type),
    mergeMap(() => {
      const url = `${baseURL}/users`;
      return ajax.getJSON(url).pipe(
        map((contacts: Contact[]) => getContactsSuccess(contacts)),
        catchError((error) => {
          return of(
            getContactsFailure(error.message || "Could not load contacts."),
          );
        }),
      );
    }),
  );
};

const getContactsByOperatorNameEpic = (action$, _state, { ajax }) => {
  return action$.pipe(
    ofType(getContactsByOperatorNameRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          operatorName: string;
        }>,
      ) => {
        const { operatorName } = action.payload;
        const url = `${baseURL}/organizations/${operatorName}/members`;
        return ajax.getJSON(url).pipe(
          map((contacts: Contact[]) =>
            getContactsByOperatorNameSuccess({ operatorName, contacts }),
          ),
          catchError((error) =>
            of(
              getContactsByOperatorNameFailure(
                error.message ||
                  "Could not load contacts for the selected organization.",
              ),
            ),
          ),
        );
      },
    ),
  );
};

const addContactEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(addContactRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          contact: Contact;
        }>,
      ) => {
        const { contact } = action.payload;
        const url = `${baseURL}/users`;

        return ajax({
          url,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: contact,
          responseType: "json",
        }).pipe(
          mergeMap((response: { response: any; message?: string }) =>
            of(
              addContactSuccess(response.response, {
                notificationOnSuccess: "Contact added successfully.",
              }),
            ),
          ),
          catchError((error) =>
            of(
              addContactFailure(
                error.response?.message ||
                  error.message ||
                  "Could not add contact.",
              ),
            ),
          ),
        );
      },
    ),
  );

const updateContactEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(updateContactRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          contact: Contact;
        }>,
      ) => {
        const { contact } = action.payload;
        const url = `${baseURL}/users/${contact?.id}`;

        return ajax({
          url,
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: contact,
          responseType: "json",
        }).pipe(
          mergeMap((response: { response: any; message?: string }) =>
            of(
              updateContactSuccess(response.response, {
                notificationOnSuccess: "Contact updated successfully",
              }),
            ),
          ),
          catchError((error) =>
            of(
              updateContactFailure(
                error.response?.message ||
                  error.message ||
                  "Could not update contact.",
              ),
            ),
          ),
        );
      },
    ),
  );

const deleteContactEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(deleteContactRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          id: number;
        }>,
      ) => {
        const { id } = action.payload;
        const url = `${baseURL}/users/${id}`;

        return ajax({ url, method: "DELETE" }).pipe(
          mergeMap(() =>
            of(
              deleteContactSuccess(id, {
                notificationOnSuccess: "Contact deleted successfully",
              }),
            ),
          ),
          catchError((error) =>
            of(
              deleteContactFailure(
                error.response?.message ||
                  error.message ||
                  "Could not delete contact.",
              ),
            ),
          ),
        );
      },
    ),
  );

const deleteOrganizationEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(deleteOrganizationRequest.type),
    mergeMap(
      (
        action: PayloadAction<{
          id: number;
        }>,
      ) => {
        const { id } = action.payload;
        const url = `${baseURL}/organizations/${id}`;

        return ajax({ url, method: "DELETE" }).pipe(
          mergeMap(() =>
            of(
              deleteOrganizationSuccess(id, {
                notificationOnSuccess: "Organization deleted successfully",
              }),
            ),
          ),
          catchError((error) =>
            of(
              deleteOrganizationFailure(
                error.response?.message ||
                  error.message ||
                  "Could not delete organization.",
              ),
            ),
          ),
        );
      },
    ),
  );

export default {
  actions: phonebookSlice.actions,
  reducer: phonebookSlice.reducer,
  middleware: [
    getOrganizationsEpic,
    addOrganizationEpic,
    updateOrganizationEpic,
    deleteOrganizationEpic,
    getContactsEpic,
    getContactsByOperatorNameEpic,
    addContactEpic,
    updateContactEpic,
    deleteContactEpic,
  ],
  selectors: phonebookSlice.selectors,
};
